import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { extractErrorMessage } from "../utils/errorHandler";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../styles/auth.css";
import "../styles/forms/phone-input.css";
import {
   ERR_PHONE_INVALID,
   ERR_PASSWORD_MIN_LENGTH,
   ERR_LOGIN_FAILED,
   ERR_ENTER_6_DIGIT_CODE,
   ERR_RESEND_CODE_FAILED,
   ERR_INVALID_VERIFICATION_CODE,
} from "../utils/errorMessages.js";

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
   const [step, setStep] = useState("login"); // 'login' or 'otp'
   const [formData, setFormData] = useState({
      phone: "",
      password: "",
   });
   const [error, setError] = useState("");
   const [isLoading, setIsLoading] = useState(false);

   // OTP related state
   const [otp, setOtp] = useState("");
   const [otpLoading, setOtpLoading] = useState(false);
   const [resendTimer, setResendTimer] = useState(0);
   const [canResend, setCanResend] = useState(false);
   const [userPhone, setUserPhone] = useState("");

   const modalBodyRef = useRef();
   const otpInputRef = useRef(null);

   // Use auth context
   const { signIn, verifyOTP, sendOTP } = useAuth();

   // Reset modal state when closed
   useEffect(() => {
      if (!isOpen) {
         setStep("login");
         setFormData({
            phone: "",
            password: "",
         });
         setError("");
         setIsLoading(false);
         // Reset OTP state
         setOtp("");
         setOtpLoading(false);
         setResendTimer(0);
         setCanResend(false);
         setUserPhone("");
      }
   }, [isOpen]);

   // OTP Timer
   useEffect(() => {
      let interval = null;
      if (resendTimer > 0) {
         interval = setInterval(() => {
            setResendTimer(resendTimer - 1);
         }, 1000);
      } else if (resendTimer === 0 && step === "otp") {
         setCanResend(true);
      }
      return () => clearInterval(interval);
   }, [resendTimer, step]);

   // Auto-focus first OTP input when step changes to OTP
   useEffect(() => {
      if (step === "otp" && otpInputRef.current) {
         setTimeout(() => {
            otpInputRef.current.focus();
         }, 100);
      }
   }, [step]);

   // Auto-scroll to top when error appears
   useEffect(() => {
      if (error && modalBodyRef.current) {
         modalBodyRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
         });
      }
   }, [error]);

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));
      setError("");
   };

   const handlePhoneChange = (value) => {
      // react-phone-number-input returns the value in E.164 format (+12133734253)
      // or undefined if invalid
      setFormData((prev) => ({
         ...prev,
         phone: value || "",
      }));
      setError("");
   };

   // OTP Input Handlers
   const handleOtpChange = (e) => {
      const { value } = e.target;
      if (/^\d{0,6}$/.test(value)) {
         setOtp(value);
         setError("");
      }
   };

   const handleOtpKeyDown = (e) => {
      if (e.key === "Enter" && otp.length === 6) {
         handleOtpSubmit();
      }
   };

   const startResendTimer = () => {
      setResendTimer(30);
      setCanResend(false);
   };

   const handleResendOtp = async () => {
      try {
         setError("");

         console.log("📱 Resending OTP to:", userPhone);
         const response = await sendOTP(userPhone);

         if (response.success) {
            console.log("✅ OTP resent successfully");
            startResendTimer();
         } else {
            throw new Error(response.message || ERR_RESEND_CODE_FAILED);
         }
      } catch (err) {
         console.error("Resend OTP error:", err);
         const errorMessage = extractErrorMessage(err, ERR_RESEND_CODE_FAILED);
         setError(errorMessage);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setError("");
      setIsLoading(true);

      try {
         // Validate phone number format
         if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
            setError(ERR_PHONE_INVALID);
            setIsLoading(false);
            return;
         }

         // Client-side password validation
         if (formData.password.length < 8) {
            setError(ERR_PASSWORD_MIN_LENGTH);
            setIsLoading(false);
            return;
         }

         const response = await signIn(formData.phone, formData.password);

         console.log("🔍 Login response:", {
            otp_sent: response.otp_sent,
            requires_otp: response.requires_otp,
            message: response.message,
         });

         // Check if OTP verification is required
         if (response.otp_sent && response.requires_otp) {
            console.log("📱 OTP sent after login, moving to verification");
            setUserPhone(response.user.phone || formData.phone);
            setStep("otp");
            setIsLoading(false);
            startResendTimer();
         } else {
            console.log("⚠️ No OTP required, completing login directly");
            // Complete login process directly (fallback case)
            onLoginSuccess(response.user);
         }
      } catch (err) {
         const errorMessage = extractErrorMessage(err, ERR_LOGIN_FAILED);
         setError(errorMessage);
         setIsLoading(false);
      }
   };

   const handleOtpSubmit = async () => {
      const otpCode = otp;

      if (otpCode.length !== 6) {
         setError(ERR_ENTER_6_DIGIT_CODE);
         return;
      }

      setOtpLoading(true);
      setError("");

      try {
         console.log("🔍 Verifying OTP for phone:", userPhone);
         const response = await verifyOTP(userPhone, otpCode);

         console.log("✅ OTP verified:", otpCode);
         // Complete login process
         onLoginSuccess(response.user);
         onClose();
      } catch (err) {
         const errorMessage = extractErrorMessage(err, ERR_INVALID_VERIFICATION_CODE);
         setError(errorMessage);
      } finally {
         setOtpLoading(false);
      }
   };

   if (!isOpen) return null;

   return (
      <div className="auth-modal-overlay">
         <div className="auth-modal">
            <div className="auth-modal-header">
               <h2>{step === "otp" ? "Verify Your Phone" : "Sign In"}</h2>
               <p className="auth-modal-subtitle">
                  {step === "otp"
                     ? `We sent a 6-digit code to ${userPhone}`
                     : "Enter your credentials to sign in"}
               </p>
               <button className="close-button" onClick={onClose}>
                  ×
               </button>
            </div>

            <div className="auth-modal-body" ref={modalBodyRef}>
               {error && (
                  <div className="auth-error-message">
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                     </svg>
                     {error}
                  </div>
               )}

               {step === "otp" ? (
                  <div className="auth-otp-section">
                     <div className="auth-otp-inputs">
                        <input
                           ref={otpInputRef}
                           type="text"
                           autoComplete="one-time-code"
                           inputMode="numeric"
                           value={otp}
                           onChange={handleOtpChange}
                           onKeyDown={handleOtpKeyDown}
                           className="auth-otp-input"
                           maxLength="6"
                           disabled={otpLoading}
                           placeholder="------"
                           style={{
                              width: "100%",
                              textAlign: "center",
                              letterSpacing: "0.5em",
                              fontSize: "1.2rem",
                           }}
                        />
                     </div>

                     <div className="auth-otp-actions">
                        <button
                           type="button"
                           onClick={handleOtpSubmit}
                           className="auth-btn-primary"
                           disabled={otp.length !== 6 || otpLoading}
                        >
                           {otpLoading ? (
                              <div className="auth-loading">
                                 <div className="auth-spinner"></div>
                                 Verifying...
                              </div>
                           ) : (
                              <>
                                 <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    style={{ width: "16px", height: "16px" }}
                                 >
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth="2"
                                       d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                 </svg>
                                 Verify & Sign In
                              </>
                           )}
                        </button>

                        <div className="auth-otp-resend">
                           {resendTimer > 0 ? (
                              <span className="auth-timer">Resend code in {resendTimer}s</span>
                           ) : (
                              <button
                                 type="button"
                                 onClick={handleResendOtp}
                                 className="auth-link-button"
                                 disabled={!canResend}
                              >
                                 Didn't receive the code? Send again
                              </button>
                           )}
                        </div>

                        <button
                           type="button"
                           onClick={() => setStep("login")}
                           className="auth-link-button"
                           style={{ marginTop: "12px" }}
                        >
                           <svg
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              style={{ width: "14px", height: "14px" }}
                           >
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth="2"
                                 d="M15 19l-7-7 7-7"
                              />
                           </svg>
                           Back to login
                        </button>
                     </div>
                  </div>
               ) : (
                  <form onSubmit={handleSubmit} className="auth-form">
                     <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <PhoneInput
                           id="phone"
                           name="phone"
                           value={formData.phone}
                           onChange={handlePhoneChange}
                           placeholder="(555) 123-4567"
                           defaultCountry="US"
                           limitMaxLength={true}
                           required
                        />
                     </div>

                     <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                           type="password"
                           id="password"
                           name="password"
                           value={formData.password}
                           onChange={handleInputChange}
                           placeholder="Enter your password"
                           required
                        />
                     </div>

                     <button type="submit" className="submit-button" disabled={isLoading}>
                        {isLoading ? (
                           <div className="button-loading">
                              <div className="button-spinner"></div>
                              Signing In...
                           </div>
                        ) : (
                           "Sign In"
                        )}
                     </button>
                  </form>
               )}
            </div>
         </div>
      </div>
   );
};

export default LoginModal;
